import { getLoanDisplayListing } from 'app/lib/loan-display-listing';
import type { Loan } from 'app/types/loan';
import { addDays, startOfDay } from 'app/lib/date';
import { differenceInCalendarDays, startOfWeek } from 'date-fns';
import { getLoanCalendarEnd } from './loan-calendar-utils';

const DETAILS_WIDTH_RATIO = 0.3;
const MIN_DETAILS_WIDTH = 124;
const MAX_DETAILS_WIDTH = 180;
const MIN_DAY_WIDTH = 40;

export interface ResponsivePlannerLayout {
  detailsWidth: number;
  visibleWeeks: 1 | 2 | 3;
}

export function getResponsivePlannerLayout(containerWidth: number): ResponsivePlannerLayout {
  const detailsWidth = Math.round(
    Math.min(MAX_DETAILS_WIDTH, Math.max(MIN_DETAILS_WIDTH, containerWidth * DETAILS_WIDTH_RATIO)),
  );
  const timelineWidth = Math.max(0, containerWidth - detailsWidth);

  return {
    detailsWidth,
    visibleWeeks: timelineWidth >= MIN_DAY_WIDTH * 21 ? 3 : timelineWidth >= MIN_DAY_WIDTH * 14 ? 2 : 1,
  };
}

export function getVisiblePlannerRangeStart(rangeStart: Date, visibleDayCount: number, today: Date) {
  const isCurrentFiveDayWeek =
    visibleDayCount === 5 && rangeStart.getTime() === startOfWeek(today, { weekStartsOn: 1 }).getTime();
  const weekendOffset = today.getDay() === 0 ? 2 : today.getDay() - 5;

  return isCurrentFiveDayWeek && weekendOffset > 0 ? addDays(rangeStart, weekendOffset) : rangeStart;
}

export function groupLoansByListing(loans: Loan[]) {
  const groupedLoans = new Map<Loan['listingId'], Loan[]>();

  loans.forEach((loan) => {
    const displayId = getLoanDisplayListing(loan)?.id ?? loan.listingId;
    const listingLoans = groupedLoans.get(displayId);

    if (listingLoans) {
      listingLoans.push(loan);
    } else {
      groupedLoans.set(displayId, [loan]);
    }
  });

  return [...groupedLoans.values()].map((listingLoans) =>
    listingLoans.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
  );
}

export function getLoanTimelineLanePosition(
  dates: Date[],
  loan: Pick<Loan, 'start' | 'end' | 'returned' | 'status'>,
): { left: number; width: number } {
  const firstCalendarDay = dates[0];
  const lastCalendarDay = dates[dates.length - 1];
  if (!firstCalendarDay || !lastCalendarDay) return { left: 0, width: 0 };

  const firstDay = startOfDay(firstCalendarDay);
  const lastDay = startOfDay(lastCalendarDay);
  const today = startOfDay(new Date());
  const loanStart = startOfDay(new Date(loan.start));
  const loanEnd = getLoanCalendarEnd(loan, today);

  if (loanStart > lastDay || loanEnd < firstDay) return { left: 0, width: 0 };

  const visibleStart = loanStart < firstDay ? firstDay : loanStart;
  const visibleEnd = loanEnd > lastDay ? lastDay : loanEnd;
  const totalDays = differenceInCalendarDays(lastDay, firstDay) + 1;
  const left = (differenceInCalendarDays(visibleStart, firstDay) / totalDays) * 100;
  const width = ((differenceInCalendarDays(visibleEnd, visibleStart) + 1) / totalDays) * 100;

  return { left, width };
}

export function getLoanTimelineContinuation(dates: Date[], loan: Pick<Loan, 'start' | 'end' | 'returned' | 'status'>) {
  const first = dates[0];
  const last = dates[dates.length - 1];
  return {
    before: Boolean(first && startOfDay(new Date(loan.start)) < startOfDay(first)),
    after: Boolean(last && getLoanCalendarEnd(loan) > startOfDay(last)),
  };
}

// Virtualized periods: only neighboring pages mount, not this entire date range.
// Five-day pages provide over a century of history in either direction.
export const CALENDAR_INITIAL_PAGE = 10_000;
export const CALENDAR_PAGE_COUNT = CALENDAR_INITIAL_PAGE * 2 + 1;

export function getCalendarPageIndex(offsetX: number, viewportWidth: number) {
  if (viewportWidth <= 0) return CALENDAR_INITIAL_PAGE;
  return Math.max(0, Math.min(CALENDAR_PAGE_COUNT - 1, Math.round(offsetX / viewportWidth)));
}
